<template>
    <div class="content-root">
        <div
            v-if="largeCardId !== 'None'"
            class="content large-mode"
            :class="{ 'meteor-large': largeCardId === 'meteor' }"
        >
            <div class="cards large">
                <WeatherCard class="weather-card" :cardId="largeCardId" cardSize="large" />
            </div>
        </div>

        <div v-else-if="activeTheme === 'all'" class="content carousel-mode">
            <div class="cards small">
                <ArrowButton cardSize="small" direction="left" />
                <WeatherCard
                    class="weather-card"
                    v-for="card in currentCardListSmall"
                    :key="card.id"
                    :cardId="card.id"
                    cardSize="small"
                />
                <ArrowButton cardSize="small" direction="right" />
            </div>

            <div class="cards medium">
                <ArrowButton cardSize="medium" direction="left" />
                <WeatherCard
                    class="weather-card"
                    v-for="card in currentCardListMedium"
                    :key="card.id"
                    :cardId="card.id"
                    cardSize="medium"
                />
                <ArrowButton cardSize="medium" direction="right" />
            </div>
        </div>

        <!-- Energy theme: reuse two-row carousel layout (small row + medium row) -->
        <div v-else-if="gridVariant === 'grid-energy'" class="content carousel-mode">
            <div class="cards small themed-cards">
                <WeatherCard
                    class="weather-card"
                    v-for="card in themedSmallCards"
                    :key="card.id"
                    :cardId="card.id"
                    cardSize="small"
                />
            </div>
            <div class="cards medium themed-cards">
                <WeatherCard
                    class="weather-card"
                    v-for="card in themedMediumCards"
                    :key="card.id"
                    :cardId="card.id"
                    cardSize="medium"
                />
            </div>
        </div>

        <!-- Microgrid: energy flow diagram on top row, supporting cards below -->
        <div v-else-if="gridVariant === 'grid-microgrid'" class="content microgrid-mode">
            <div v-if="microgridFlowCard" class="cards flow-row">
                <WeatherCard
                    class="weather-card"
                    :cardId="microgridFlowCard.id"
                    cardSize="large"
                />
            </div>
            <div class="cards small themed-cards">
                <WeatherCard
                    class="weather-card"
                    v-for="card in microgridSmallCards"
                    :key="card.id"
                    :cardId="card.id"
                    cardSize="small"
                />
            </div>
        </div>

        <!-- Environment (3 medium, 1 row), disaster (3 medium, 1 row), meteor (1 large) -->
        <div v-else class="content themed-container" :class="gridVariant">
            <WeatherCard
                class="weather-card"
                v-for="card in themedCardList"
                :key="card.id"
                :cardId="card.id"
                :cardSize="card.cardSize"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import ArrowButton from '@/components/common/ArrowButton.vue'
import WeatherCard from '@/components/common/WeatherCard.vue'
import { THEME_MAP } from '@/themes'

export default {
    name: 'Content',
    components: {
        WeatherCard,
        ArrowButton,
    },
    props: {
        activeTheme: {
            type: String,
            default: 'all',
        },
    },
    computed: {
        ...mapState({
            largeCardId: (state) => state.largeCardId,
        }),
        ...mapGetters(['getDisplayCardList', 'getCardList']),

        currentCardListSmall() {
            return this.getDisplayCardList('small')
        },
        currentCardListMedium() {
            return this.getDisplayCardList('medium')
        },

        gridVariant() {
            const cards = this.themedCardList
            if (!cards.length) return ''
            // 微電網有自己的版型（上大下小），且同時含 large 與 small，
            // 必須早於下面的通則判斷，否則會被歸成 grid-single 只剩能流圖
            if (this.activeTheme === 'microgrid') return 'grid-microgrid'
            const sizes = cards.map((c) => c.cardSize)
            if (sizes.includes('large')) return 'grid-single'
            if (sizes.includes('small') && sizes.includes('medium')) return 'grid-energy'
            return 'grid-3cols'
        },

        // 能流圖可能因站點排除而不存在，故不寫死 id
        microgridFlowCard() {
            return this.themedCardList.find((c) => c.cardSize === 'large')
        },

        microgridSmallCards() {
            return this.themedCardList.filter((c) => c.cardSize !== 'large')
        },

        themedSmallCards() {
            return this.themedCardList.filter((c) => c.cardSize === 'small')
        },

        themedMediumCards() {
            return this.themedCardList.filter((c) => c.cardSize === 'medium')
        },

        themedCardList() {
            // 【修復 2】把 || this.activeTheme === 'meteor' 拿掉，允許流星渲染
            if (this.activeTheme === 'all') return []

            const allCards = this.getCardList()
            const themeCardIds = THEME_MAP[this.activeTheme] || []

            return themeCardIds
                .map((id) => {
                    const card = allCards.find((c) => c.id === id)
                    if (!card) return null

                    // 地質防災的雨量卡放大成 medium；氣象監測的雨量卡維持 small
                    if (this.activeTheme === 'disaster' && card.id === 'rain') {
                        return {
                            ...card,
                            cardSize: 'medium',
                        }
                    }
                    // 環境品質的水質卡放大成 medium
                    if (this.activeTheme === 'environment' && card.id === 'water') {
                        return {
                            ...card,
                            cardSize: 'medium',
                        }
                    }

                    // 【修復 3】流星主題時，將流星卡片強制變為 large 全滿版
                    if (this.activeTheme === 'meteor' && card.id === 'meteor') {
                        return {
                            ...card,
                            cardSize: 'large',
                        }
                    }

                    return card
                })
                .filter(Boolean)
        },
    },
    methods: {
        ...mapMutations(['setLargeCardId']),
    },
}
</script>

<style lang="scss" scoped>
.content-root {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.content {
    height: 100%;
    width: 100%;
}

.large-mode {
    display: flex;
    justify-content: center;
    align-items: center;
}

/* 流星大圖：取消白卡限制，讓影像吃滿可用空間 */
.large-mode.meteor-large .cards.large {
    height: 100%;
    width: 100%;
}

.carousel-mode {
    display: flex;
    flex-direction: column;
    flex-flow: row wrap;
    justify-content: space-around;
    align-items: center;
    align-content: space-around;
}

.cards {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    align-content: stretch;
    &.small {
        height: 40%;
        width: 95%;
    }
    &.medium {
        height: 50%;
        width: 95%;
    }
    &.large {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 90%;
        width: 95%;
        margin: 0 auto;
    }
    &.themed-cards {
        justify-content: space-evenly;
    }
}

/* 輪播的中卡排是「寬扁」的（各佔 45% 寬、半個版面高），儀表擺上半部只分得到
   一半高度，等比縮放後小得看不清楚。這一排改成儀表左、趨勢右，儀表就能吃滿
   整張卡的高度，實測比上下排大約六成。
   儀表畫布是 260×205，寬高比 1.27，給 55% 寬剛好讓它的高度用滿；剩下 45%
   放 13 根長條也還有每根 25px。
   環境品質那頁的卡雖然同樣是 medium，但它是「窄高」的（1/3 寬、整個版面高），
   上下排才對，所以這條規則只掛在 carousel-mode 底下，不動 themed-container。 */
.carousel-mode .cards.medium :deep(.gauge-trend-wrapper) {
    flex-direction: row;
    align-items: stretch;
    gap: 0.75rem;

    .gauge-part {
        flex: 1 1 55%;
        min-width: 0;
    }

    .trend-part {
        flex: 1 1 45%;
        min-width: 0;
    }
}

.themed-container {
    height: 100%;
    width: 100%;
    display: grid;
    gap: 1.5vh 1.5vw;
    padding: 1.5vh 2.5vw;
    box-sizing: border-box;
    overflow: hidden;

    :deep(.card-wrapper) {
        &.small,
        &.medium,
        &.large {
            height: 100%;
            width: 100%;
            min-width: unset;
            min-height: 0;
        }
    }

    &.grid-3cols {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: 1fr;
    }

    &.grid-single {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
    }
}

/* 微電網：上方能流圖一列，下方支援卡片一列。
   用 flex 配額搭配 gap，不能用「52% + 44% + space-around」——
   百分比高度加上分配間距會超出容器，小卡列會壓在能流圖卡下緣上。 */
.microgrid-mode {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5vh;
    padding: 1vh 0;
    box-sizing: border-box;

    /* 能流圖本身約 4:1。卡片比例若比圖形更扁，SVG 會改由高度決定大小，
       左右留下大片空白；故把寬度收到與圖形比例相近，讓寬度成為限制邊。 */
    .flow-row {
        flex: 0 0 56%;
        width: 62%;
        min-height: 0;
        justify-content: center;
    }

    /* 能流圖以 large 尺寸渲染，但這裡是列內元素，需吃滿整列而非視窗 */
    .flow-row :deep(.card-wrapper.large) {
        height: 100%;
        width: 100%;
    }

    /* 標題吃掉的高度直接換算成圖形尺寸，故壓縮到夠用即可 */
    .flow-row :deep(.title) {
        font-size: 1.35rem;
        padding: 0.7rem 0;
    }

    .flow-row :deep(.graph) {
        padding: 0.25rem 0.5rem 0.75rem;
        width: 96%;
    }

    .cards.small {
        flex: 1 1 auto;
        width: 95%;
        min-height: 0;
    }
}
</style>
